import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable, Subject, Subscription } from 'rxjs';
import { searchCriteriaInitial } from 'src/app/components/search/detail/variables';
import * as myGlobals from 'src/app/globals';
import { FilterCondition } from 'src/app/models/common.model';
import { CreateFolderModel } from 'src/app/models/create-folder.model';
import { FolderHierarchy } from 'src/app/models/folder-hierarchy.model';
import { CreateFolderResponseModel, HttpBulkResponseModel, HttpPaginationModel, HttpResponseModel, SearchRequestBody } from 'src/app/models/response.model';
import {
  EncryptedMoveModel,
  FolderHierarchyModel,
  ObjectDetailsModel,
  ObjectDownloadInfo,
  OfficeDocumentKey,
  StorageModel,
  StorageRequestBody,
} from 'src/app/models/storage.model';
import { VersionListModel } from 'src/app/models/version.model';
import { NotificationService } from 'src/app/services/notification.service';
import { SearchService } from 'src/app/services/search.service';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as SgeCrypto from 'src/assets/lib/sge/sge-crypto';

import { StorageComponent } from './storage.component';
@Injectable({ providedIn: 'root' })
export class MyStorageService implements OnDestroy {
  subscription: Subscription = new Subscription();
  searchSubscription: Subscription = new Subscription();
  detailSearchSubscription: Subscription = new Subscription();
  storageSource = new Subject<StorageComponent | null>();

  searchText?: string;
  isDetailedSearch = false;

  searchCriteria: SearchRequestBody = { ...searchCriteriaInitial };

  constructor(
    private http: HttpClient,
    private notificationService: NotificationService,
    private router: Router,
    private searchService: SearchService,
    private translateService: TranslateService,
  ) {
    this.searchSubscription = this.searchService.searchText$.subscribe((val) => {
      this.searchText = val;
    });
    this.detailSearchSubscription.add(
      this.searchService.searchCriteria$.subscribe((val: SearchRequestBody) => {
        this.searchCriteria = val;
      }),
    );
    this.detailSearchSubscription.add(
      this.searchService.isDetailSearching$.subscribe((val: boolean) => {
        this.isDetailedSearch = val;
      }),
    );
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    if (this.detailSearchSubscription) {
      this.detailSearchSubscription.unsubscribe();
    }
  }

  changeStorage(storage: any): void {
    this.storageSource.next(storage);
  }

  getFoldersAndFiles(folderParams?: HttpPaginationModel, filter?: StorageRequestBody): Observable<HttpResponseModel<StorageModel[]>> {
    let params: HttpParams = new HttpParams();
    const isDirectory: FilterCondition | undefined = filter?.filterCriterias.find((x) => x.fieldName === 'directory');
    const isSearching = (this.searchText && this.searchText.length > 0) || this.isDetailedSearch;
    const url: string = isSearching ? '/search/grid-view' : '/objects/filter';
    if (folderParams) {
      params = params.set('size', folderParams.pageSize).set('page', folderParams.pageNumber).set('sortField', 'modifyDate');
      if (isSearching) {
        if (folderParams.sortField && folderParams.sortDirection) {
          if (folderParams.sortField === 'name') {
            params = params.set('sortField', 'name.keyword');
          } else {
            params = params.set('sortField', folderParams.sortField);
          }
          params = params.set('sortDirection', folderParams.sortDirection === 'Ascending' ? 'Asc' : 'Desc');
        }
        params = params.set('detailed', this.isDetailedSearch);
        if (isDirectory) {
          params = params.set('directory', !!isDirectory.fieldValue);
        }
      } else {
        if (folderParams.sortField && folderParams.sortDirection) {
          params = params.set('sortField', folderParams.sortField).set('sortDirection', folderParams.sortDirection);
        }
      }
    }
    if (this.searchText && this.searchText.length > 0) {
      this.searchCriteria.name = this.searchText;
      this.searchCriteria.content = this.searchText;
    }
    const body = !isSearching
      ? {
          shareInfoRequired: filter?.shareInfoRequired || false,
          filterCriterias: filter?.filterCriterias || [],
        }
      : this.searchCriteria;

    return this.http.post<HttpResponseModel<StorageModel[]>>(myGlobals.Config.API + url, body, { params });
  }

  getFolderHierarchy(encrypted: boolean): Observable<FolderHierarchy> {
    const param = new HttpParams().set('encrypted', encrypted.toString());

    return this.http.get<FolderHierarchy>(`${myGlobals.Config.API}/objects/hierarchy`, {
      params: param,
    });
  }

  moveFolder(sourceId: string, destinationId: string): Observable<HttpBulkResponseModel> {
    return this.http.put<HttpBulkResponseModel>(`${myGlobals.Config.API}/objects/${sourceId.toString()}/move` + (destinationId ? `?destinationObjectId=${destinationId}` : ''), {});
  }

  multiMoveFolder(ids: string[], destinationId?: string): Observable<HttpBulkResponseModel> {
    return this.http.put<HttpBulkResponseModel>(`${myGlobals.Config.API}/objects/bulk/move` + (destinationId ? `?destinationObjectId=${destinationId}` : ''), { ids });
  }

  moveFolderFunction(selectedItemListId: string[], destinationId: string, encryptedObj: EncryptedMoveModel): Promise<void> {
    const isSafeLockEnabled = this.router.url.startsWith('/sifreli-depo/liste');

    return new Promise((resolve) => {
      if (selectedItemListId.length > 1) {
        this.subscription.add(
          this.multiMoveFolder(selectedItemListId, destinationId).subscribe({
            next: (res: HttpBulkResponseModel) => {
              let notificationMessage = '';
              if (res.successList.length > 0 && res.failList.length === 0 && !isSafeLockEnabled) {
                notificationMessage += this.translateService.instant('OPERATION_SUCCESSFUL') + '\n';
                if (notificationMessage) {
                  this.notificationService.successNotification({ message: notificationMessage });
                }
              } else if (res.successList.length === 0 && res.failList.length > 0 && !isSafeLockEnabled) {
                notificationMessage = this.getExistingFailListMessages(res.failList, notificationMessage, false);
                if (notificationMessage) {
                  this.notificationService.errorNotification({ message: notificationMessage });

                  return;
                }
              } else if (res.successList.length > 0 && res.failList.length > 0 && !isSafeLockEnabled) {
                notificationMessage += this.translateService.instant('SUCCESSFUL_TRANSACTIONS_AVAILABLE') + '\n';
                notificationMessage = this.getExistingFailListMessages(res.failList, notificationMessage, false);
                if (notificationMessage) {
                  this.notificationService.errorNotification({ message: notificationMessage });
                }
              } else if (res.successList.length > 0 && isSafeLockEnabled && res.failList.length > 0) {
                notificationMessage += this.translateService.instant('SUCCESSFUL_TRANSACTIONS_AVAILABLE') + '\n';
                let ids = res.successList.map((x) => x.encryptedId);
                ids = ids.filter((x) => x && x.length > 0);
                if (res.failList.length > 0) {
                  notificationMessage = this.getExistingFailListMessages(res.failList, notificationMessage, false);
                  this.moveEncryptedFolderFunction(ids, encryptedObj, notificationMessage);
                } else {
                  this.moveEncryptedFolderFunction(ids, encryptedObj, notificationMessage);
                }
              } else if (res.successList.length === 0 && res.failList.length > 0 && isSafeLockEnabled) {
                notificationMessage = this.getExistingFailListMessages(res.failList, notificationMessage, false);
                if (notificationMessage) {
                  this.notificationService.errorNotification({ message: notificationMessage });
                }
              }

              resolve();
            },
            complete: () => {
              resolve();
            },
          }),
        );
      } else {
        this.subscription.add(
          this.moveFolder(selectedItemListId[0], destinationId).subscribe({
            next: () => {
              const message = this.translateService.instant('OPERATION_SUCCESSFUL') + '\n';

              if (isSafeLockEnabled && encryptedObj.encryptedIds) {
                this.moveEncryptedFolderFunction(encryptedObj.encryptedIds, encryptedObj, message);
              } else {
                this.notificationService.successNotification({ message });
              }
              resolve();
            },
            complete: () => {
              resolve();
            },
          }),
        );
      }
    });
  }

  moveEncryptedFolderFunction(ids: (string | null)[], encryptedObj: EncryptedMoveModel, message?: string): void {
    SgeCrypto.moveEncryptedNodes(encryptedObj.parentId, encryptedObj.destinationId, ids)
      .then((result: any) => {
        if (result) {
          if (message) {
            this.notificationService.successNotification({ message });
          }
        } else {
          const notification = {
            message: this.translateService.instant('FOLDER_MOVE_FAILED'),
          };
          this.notificationService.errorNotification(notification);
        }
      })
      .catch(() => {
        const notification = {
          message: this.translateService.instant('ERROR_OCCURRED'),
        };
        this.notificationService.errorNotification(notification);
      });
  }

  getExistingFailListMessages(items: any[], notificationMessage = '', copy: boolean, restore?: boolean): string {
    const destinationItems = items.filter(({ cause }: { cause: string; directory?: boolean }) => cause === 'DESTINATION_MUST_NOT_BE_UNDER_SOURCE');
    const destNames = destinationItems.map((item) => item.name);
    const actionText = copy
      ? this.translateService.instant('CANNOT_BE_COPIED')
      : restore
      ? this.translateService.instant('CANNOT_BE_RESTORED')
      : this.translateService.instant('CANNOT_BE_MOVED');
    if (destinationItems.length > 0) {
      notificationMessage += `\nBir klasör kendisi ve altındaki bir klasör içine ${actionText}.\n Lütfen farklı bir hedef klasör seçin. ${destNames.join(', ')}\n`;
    }

    const existingItems = items.filter(({ cause }: { cause: string; directory?: boolean }) => cause === 'OBJECT_ALREADY_EXISTS' || cause === 'EXISTS_IN_THE_DIRECTORY');
    const existingNames = existingItems.map((item) => item.name);

    if (existingItems.length > 0) {
      notificationMessage += `\n${this.translateService.instant('OBJECT_ALREADY_EXISTS')} ${existingNames.join(', ')}\n`;
    }

    const encryptedFiles = items.filter(
      ({ cause }: { cause: string; directory?: boolean }) =>
        cause === 'ENCRYPTED_FILES_NOT_MOVE_ROOT' || cause === 'ENCRYPTED_FILES_NOT_COPY_ROOT' || cause === 'ENCRYPTED_FILES_NOT_RESTORE_TRASH_OBJECT_ROOT',
    );
    if (encryptedFiles.length > 0) {
      notificationMessage += `\nŞifreli dosyalar ana dizine  ${actionText}.\n`;
    }
    const quotaLimitedFiles = items.filter(({ cause }: { cause: string; directory?: boolean }) => cause === 'USER_QUOTA_LIMIT_EXCEEDED');

    const quotaLimitedFileNames = quotaLimitedFiles.map((item) => item.name);
    if (quotaLimitedFiles.length > 0) {
      const translation = this.translateService.instant('USER_QUOTA_LIMIT_EXCEEDED').split('__');
      notificationMessage += `\n${translation[0]}${quotaLimitedFileNames.join(', ')}\n`;
    }

    const groupWithOneMember = items.filter(({ cause }: { cause: string; directory?: boolean }) => cause === 'IT_CANNOT_BE_SHARED_WITH_A_GROUP_WHOSE_ONLY_MEMBER_IS_ITSELF');

    if (groupWithOneMember.length > 0) {
      notificationMessage += `\nGrupta sadece tek üyesi olan gruplarla  dosya/klasör paylaşımı yapılamaz!\n`;
    }

    return notificationMessage;
  }

  renameFileOrFolder(objectId: string, newName: string): Observable<HttpResponseModel<StorageModel>> {
    const url = `${myGlobals.Config.API}/objects/${objectId}/rename`;

    const requestBody = { name: newName };

    return this.http.put<HttpResponseModel<StorageModel>>(url, requestBody);
  }

  changeEncrptedIds(renameObjList: any): Observable<HttpResponseModel<StorageModel>> {
    const url = `${myGlobals.Config.API}/objects/bulk/updateEncryptedId`;
    const requestBody = { ids: renameObjList };

    return this.http.put<HttpResponseModel<StorageModel>>(url, requestBody);
  }

  createFolder(createFormData: CreateFolderModel): Observable<CreateFolderResponseModel> {
    return this.http.post<CreateFolderResponseModel>(`${myGlobals.Config.API}/objects/folder-create`, createFormData);
  }

  triggerCreatingFolder(name: string, encrypted: boolean, parentId: string | null, encryptedId: string | null): Promise<CreateFolderResponseModel> {
    if (encrypted) {
      return this.createEncryptedFolderFunction(name, encrypted, parentId, encryptedId);
    } else {
      return this.createFolderFunction(name, encrypted, parentId, encryptedId);
    }
  }

  createFolderFunction(name: string, encrypted: boolean, parentId: string | null, encryptedId: string | null): Promise<CreateFolderResponseModel> {
    const createFormData: CreateFolderModel = {
      name,
      parentId,
      encrypted,
      encryptedId,
    };

    return new Promise((resolve, reject) => {
      this.subscription.add(
        this.createFolder(createFormData).subscribe({
          next: (res: CreateFolderResponseModel) => {
            resolve(res);
          },
          error: () => {
            reject();
          },
        }),
      );
    });
  }

  createEncryptedFolderFunction(name: string, encrypted: boolean, parentId: string | null, encryptedId: string | null): Promise<CreateFolderResponseModel> {
    return new Promise((resolve, reject) => {
      SgeCrypto.listEncryptedFolder(encryptedId ?? '').then((fileList: any) => {
        const fileIndex = fileList.findIndex((file: any) => file.name === name);
        if (fileIndex >= 0) {
          const notification = {
            message: this.translateService.instant('OBJECT_ALREADY_EXISTS'),
          };
          this.notificationService.warningNotification(notification);
          reject();
        } else {
          const fileId = SgeCrypto.generateId();
          this.createFolderFunction(fileId, encrypted, parentId, fileId).then((res: CreateFolderResponseModel) => {
            SgeCrypto.createEncryptedFolder(res.parentEncryptedId, fileId, name, true)
              .then(() => {
                resolve(res);
              })
              .catch(() => {
                reject();
              });
          });
        }
      });
    });
  }

  getObjectDetails(objectId: string, shareInfoRequired?: boolean): Observable<ObjectDetailsModel> {
    const params = new HttpParams();
    if (shareInfoRequired) {
      params.set('shareInfoRequired', shareInfoRequired.toString());
    }

    return this.http.get<ObjectDetailsModel>(`${myGlobals.Config.API}/objects/${objectId}/details`, { params });
  }

  getDownloadId(objectId: string): Observable<ObjectDownloadInfo> {
    const url = `${myGlobals.Config.API}/s3/downloads`;
    const objectIdList = [];
    objectIdList.push(objectId);
    const requestBody = {
      objects: objectIdList,
      aplicationName: 'storage',
      downloadType: 'INLINE',
    };

    return this.http.post<ObjectDownloadInfo>(url, requestBody);
  }

  downloadObject(downloadId: string): Observable<string> {
    const url = `${myGlobals.Config.API}/s3/downloads/${downloadId}/single-part`;

    return this.http.get(url, { responseType: 'blob' }).pipe(
      map((blob: Blob) => {
        const objectURL = URL.createObjectURL(blob);

        return objectURL;
      }),
    );
  }

  getDocumentKey(objectId: string): Observable<OfficeDocumentKey> {
    return this.http.get<OfficeDocumentKey>(`${myGlobals.Config.API}/wopi/${objectId}/getDocumentKey`, {});
  }

  deleteFolder(objectId: string): Observable<any> {
    return this.http.delete(`${myGlobals.Config.API}/objects/${objectId}/delete`, {});
  }

  trashFolder(objectId: string): Observable<any> {
    return this.http.delete(`${myGlobals.Config.API}/objects/${objectId}/trash`, {});
  }

  copyObjectToMyStorage(objectId: string, destinationId?: string): Observable<any> {
    return this.http.post(`${myGlobals.Config.API}/objects/${objectId}/copyToMyStorage` + (destinationId ? `?destinationId=${destinationId}` : ''), {});
  }

  multiCopyObjectToMyStorage(ids: string[], destinationId?: string): Observable<any> {
    return this.http.post(`${myGlobals.Config.API}/objects/bulk/copyToMyStorage` + (destinationId ? `?destinationId=${destinationId}` : ''), { ids });
  }

  getFolderPath(objectId?: string, parentId?: string, privateShareId?: string, isPublic?: boolean, shareId?: string): Observable<FolderHierarchyModel[]> {
    let params = new HttpParams();
    const url = '/objects' + (isPublic ? '/public' : '') + '/path';
    if (objectId) {
      params = params.set('childId', objectId);
    }
    if (parentId) {
      params = params.set('parentId', parentId);
    }
    if (privateShareId) {
      params = params.set('privateShareId', privateShareId);
    }
    if (shareId) {
      params = params.set('shareId', shareId);
    }

    return this.http.get<FolderHierarchyModel[]>(`${myGlobals.Config.API}${url}`, { params });
  }

  copyToDirectory(objectId: string, destinationId?: string): Observable<any> {
    return this.http.post(`${myGlobals.Config.API}/objects/${objectId}/copy` + (destinationId ? `?destinationId=${destinationId}` : ''), {});
  }

  multiCopyToDirectory(ids: string[], destinationId?: string): Observable<any> {
    return this.http.post(`${myGlobals.Config.API}/objects/bulk/copy` + (destinationId ? `?destinationId=${destinationId}` : ''), { ids });
  }

  getVersionHistory(objectId: string, folderParams?: HttpPaginationModel): Observable<HttpResponseModel<VersionListModel[]>> {
    let params: HttpParams = new HttpParams();
    if (folderParams) {
      params = params.set('size', folderParams.pageSize).set('page', folderParams.pageNumber);
    }

    return this.http.get<HttpResponseModel<any[]>>(`${myGlobals.Config.API}/objects/${objectId}/versions`, { params });
  }

  activateObjectVersion(objectId: string, versionId: string | null): Observable<any> {
    return this.http.put<HttpResponseModel<any[]>>(`${myGlobals.Config.API}/objects/${objectId}/versions/${versionId}`, {});
  }

  deleteObjectVersion(objectId: string, versionId: string | null): Observable<any> {
    return this.http.delete<HttpResponseModel<any[]>>(`${myGlobals.Config.API}/objects/${objectId}/versions/${versionId}`);
  }
}
